import { useMultipleSelection, useSelect } from 'downshift';
import { valueToArray, getItemsFilter } from './utils';
import { HTMLAttributes } from 'react';

type UseMultiSelectProps<Option extends { id: string | number }> =
  HTMLAttributes<HTMLSelectElement> & {
    options: Option[];
    separator?: string;
    defaultValue?: string | number | readonly string[];
    keepOpen?: boolean;
    value?: string | readonly string[];
    onChange?: (value: string | readonly string[]) => void;
  };

export function useMultiSelect<Option extends { id: string | number }>({
  options,
  separator = ';',
  defaultValue,
  keepOpen,
  value,
  onChange,
}: UseMultiSelectProps<Option>) {
  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection({
    initialSelectedItems: valueToArray(defaultValue, separator),
    selectedItems: valueToArray(value, separator),
    onSelectedItemsChange(changes) {
      const { selectedItems } = changes;
      if (onChange) {
        onChange(selectedItems);
      }
    },
  });
  const items = options.filter(getItemsFilter<Option>(selectedItems, 'id'));
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    selectedItem: null,
    items,
    stateReducer(_state, actionAndChanges) {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: keepOpen,
          };
      }
      return changes;
    },
    onStateChange({ type, selectedItem: newSelectedItem }) {
      switch (type) {
        case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
        case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
        case useSelect.stateChangeTypes.ItemClick:
        case useSelect.stateChangeTypes.ToggleButtonBlur:
          if (newSelectedItem) {
            addSelectedItem(String(newSelectedItem.id));
          }
          break;
        default:
          break;
      }
    },
  });

  return {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    items,
  };
}
